import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, useToast } from '../../components/common';
import {
  PRICING_MODELS, RESERVED_MODELS, MODEL_LABELS, MODEL_DESCRIPTIONS,
  CURRENCIES, isPaid, isOfferable,
} from '../../constants/formP';
import api from '../../services/api';
import { requirementLabels } from '../../utils/payoutRequirements';
import { useGroup } from '../../contexts/GroupContext';
import './PricingTab.css';

/**
 * What the app charges — authored here, rendered by the server.
 *
 * THIS FILE FORMATS NO MONEY. Every price string shown to anybody, including the live
 * preview while you type, comes back from `POST /third-party/pricing/preview`. That is
 * the same renderer the consent card and the store badge use, so what a developer sees
 * here is byte-identical to what a buyer sees. A local formatter would be a second one,
 * and then this page could promise a price the consent screen contradicts.
 *
 * MAJOR UNITS IN THE INPUTS, minor units on the wire. The developer types 4.50 and we
 * send 450, converted once at the boundary. Asking somebody to type 450 for £4.50 is how
 * an app ships priced at a hundred times what its author intended — and the store would
 * then display that faithfully.
 *
 * STAGED, like everything else on the spec. Saving collects into the one pending change
 * per app and commits as a single version alongside whatever else moved, so a price and
 * the DLR it sits beside on the consent card cannot go live at different times.
 */

/** Minor units -> the value an input should hold. Not a display formatter. */
function toMajorInput(minor, currency) {
  if (minor === null || minor === undefined) return '';
  const decimals = CURRENCIES[currency]?.decimals ?? 2;
  return (minor / 10 ** decimals).toFixed(decimals);
}

/** What the developer typed -> minor units. The one conversion. */
function toMinor(text, currency) {
  if (text === '' || text === null || text === undefined) return null;
  const decimals = CURRENCIES[currency]?.decimals ?? 2;
  const n = Number(String(text).replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 10 ** decimals);
}

export default function PricingTab({ app, onStaged }) {
  const toast = useToast();
  // ROUTED, not `window.location.href`. Both payout prompts below used to do a full
  // document load to reach /billing — in a SPA that tears down the app, refetches
  // everything and flashes, for a link to a sibling page. It also discarded any
  // unsaved pricing edit without saying so.
  const navigate = useNavigate();
  // The Money page shows the ACTIVE workspace's account, so switch to the app's owner
  // first — otherwise a group app's prompt lands on the person's account, or vice versa.
  const { switchToGroup, switchToPersonal } = useGroup();
  const toPayouts = useCallback(() => {
    if (app?.group_id) switchToGroup(app.group_id); else switchToPersonal();
    navigate('/billing');
  }, [navigate, app?.group_id, switchToGroup, switchToPersonal]);
  const [model, setModel] = useState('free');
  const [currency, setCurrency] = useState('GBP');
  const [amount, setAmount] = useState('');
  const [monthly, setMonthly] = useState('');
  const [annual, setAnnual] = useState('');
  const [trialDays, setTrialDays] = useState('');
  const [saving, setSaving] = useState(false);

  // The rendering, from the server. Never built here.
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  const [payout, setPayout] = useState(null);
  const [payoutLoading, setPayoutLoading] = useState(true);

  // Hydrate from the app's live pricing.
  useEffect(() => {
    const h = hydrate(app?.pricing);
    setModel(h.model); setCurrency(h.currency); setAmount(h.amount);
    setMonthly(h.monthly); setAnnual(h.annual); setTrialDays(h.trialDays);
  }, [app?.id, app?.pricing]);

  useEffect(() => {
    let cancelled = false;
    // The account THIS app's sales pay — the group's for a group app, as checkout does.
    api.getPayoutAccount(app?.group_id || undefined)
      .then(r => { if (!cancelled) setPayout(r); })
      .catch(() => { if (!cancelled) setPayout(null); })
      .finally(() => { if (!cancelled) setPayoutLoading(false); });
    return () => { cancelled = true; };
  }, [app?.group_id]);

  /** The blob as the API wants it. */
  const blob = useMemo(
    () => buildBlob({ model, currency, amount, monthly, annual, trialDays }),
    [model, currency, amount, monthly, annual, trialDays],
  );
  // What is live, built the same way, so "changed" means the blob would differ.
  const baseline = useMemo(() => JSON.stringify(buildBlob(hydrate(app?.pricing))), [app?.pricing]);
  const dirty = JSON.stringify(blob) !== baseline;
  const reset = () => {
    const h = hydrate(app?.pricing);
    setModel(h.model); setCurrency(h.currency); setAmount(h.amount);
    setMonthly(h.monthly); setAnnual(h.annual); setTrialDays(h.trialDays);
  };

  // Debounced live preview. Also the validator: the server returns the same message the
  // save path would, so a developer finds out here rather than on save.
  useEffect(() => {
    const t = setTimeout(() => {
      api.previewPricing(blob)
        .then(r => {
          setPreview(r?.pricing_display || null);
          setPreviewError(r?.ok ? null : r?.error || null);
        })
        .catch(() => { setPreview(null); setPreviewError(null); });
    }, 250);
    return () => clearTimeout(t);
  }, [blob]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await api.stageDraft(app.id, { pricing: blob });
      toast.success('Pricing staged — commit it to go live.');
      onStaged?.();
    } catch (e) {
      toast.error(e?.message || 'Could not stage the price.');
    } finally {
      setSaving(false);
    }
  }, [app?.id, blob, onStaged, toast]);

  const paid = isPaid(blob);
  const reserved = RESERVED_MODELS.includes(app?.pricing?.model);
  const canCharge = payout?.account?.can_charge;
  // Nothing typed yet is not an error: the server's "needs pricing.monthly" is
  // for a price someone tried to set, not for an empty form.
  const typed = model === 'one_time' ? amount !== '' : (monthly !== '' || annual !== '');
  const showError = Boolean(previewError) && (!paid || typed);
  const sym = CURRENCIES[currency]?.symbol || '';
  const initials = (app?.name || '?').trim().slice(0, 2).toUpperCase();
  const feeBps = payout?.platform_fee_bps;

  return (
    <div className="pricing-tab">
      {/* An app already carrying a model this build cannot offer. It fails CLOSED —
          unauthorizable rather than free — so say that plainly instead of rendering a
          price the platform will refuse. */}
      {reserved && !isOfferable(app?.pricing) && (
        <div className="pricing-tab__block is-warn" role="note">
          <b>This app's pricing model is not available yet.</b>
          <span>
            {MODEL_LABELS[app.pricing.model] || app.pricing.model} is designed but not
            built. Until it is, this app cannot be authorized by anyone — pick one of the
            models below.
          </span>
        </div>
      )}

      <section className="pricing-tab__section">
        <h3>What it costs</h3>
        {/* Choice cards, each saying what the model means, rather than a strip
            of three small words with the meaning in a line underneath. */}
        <div className="pricing-tab__models" role="radiogroup" aria-label="What it costs">
          {PRICING_MODELS.map(m => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={model === m}
              className={`pricing-tab__model${model === m ? ' is-on' : ''}`}
              onClick={() => setModel(m)}
            >
              {/* Drawn, not a rounded box: the kit squares every corner inside .blocks. */}
              <svg className="pricing-tab__model-dot" viewBox="0 0 16 16" aria-hidden="true">
                <circle cx="8" cy="8" r="6.75" />
                {model === m && <circle className="is-dot" cx="8" cy="8" r="3.5" />}
              </svg>
              <span>
                <b>{MODEL_LABELS[m]}</b>
                <span>{MODEL_DESCRIPTIONS[m]}</span>
              </span>
            </button>
          ))}
        </div>
      </section>

      {paid && (
        <section className="pricing-tab__section">
          <h3>Amount</h3>
          {/* Typed in pounds, sent in pence — said here so nobody "helpfully"
              changes the inputs to minor units. */}
          <p className="pricing-tab__help">
            Type it the way you would say it ({sym}4.50 is {sym}4.50). Each currency is priced on its own, never converted.
          </p>
          <div className={`pricing-tab__fields${model === 'one_time' ? ' is-two' : ''}`}>
            <label className="pricing-tab__field">
              <span>Currency</span>
              <select value={currency} onChange={e => setCurrency(e.target.value)}>
                {Object.keys(CURRENCIES).map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>

            {model === 'one_time' ? (
              <label className="pricing-tab__field">
                <span>Price</span>
                <span className="pricing-tab__money">
                  <i>{sym}</i>
                  <input value={amount} onChange={e => setAmount(e.target.value)}
                         inputMode="decimal" placeholder="12.00" />
                </span>
              </label>
            ) : (
              <>
                <label className="pricing-tab__field">
                  <span>Monthly</span>
                  <span className="pricing-tab__money">
                    <i>{sym}</i>
                    <input value={monthly} onChange={e => setMonthly(e.target.value)}
                           inputMode="decimal" placeholder="4.00" />
                  </span>
                </label>
                <label className="pricing-tab__field">
                  <span>Yearly <em>optional</em></span>
                  <span className="pricing-tab__money">
                    <i>{sym}</i>
                    <input value={annual} onChange={e => setAnnual(e.target.value)}
                           inputMode="decimal" placeholder="40.00" />
                  </span>
                </label>
                <label className="pricing-tab__field">
                  <span>Free trial</span>
                  <span className="pricing-tab__money is-suffix">
                    <input value={trialDays} onChange={e => setTrialDays(e.target.value)}
                           inputMode="numeric" placeholder="0" />
                    <i>days</i>
                  </span>
                </label>
              </>
            )}
          </div>
        </section>
      )}

      {/* THE PREVIEW IS THE PRODUCT. The card on the right is the literal text a
          person reads on the consent screen, rendered by the server rather than
          approximated here. Beside it: whether the money has somewhere to go. */}
      <section className="pricing-tab__section">
        <h3>What people will see</h3>
        <div className="pricing-tab__see">
          <div className="pricing-tab__facts">
            {!paid ? (
              <p className="pricing-tab__help">
                A free app shows no price line at all — the absence is the information.
                Free apps need no payout account.
              </p>
            ) : (
              <>
                {/* WHERE THE MONEY GOES — on the same page as the price, because a
                    price with nowhere to be paid is not a sellable app: the checkout
                    refuses it. */}
                <div className="pricing-tab__fact">
                  <span>Payouts</span>
                  {payoutLoading ? (
                    <b className="is-quiet">Checking…</b>
                  ) : !payout?.account ? (
                    <div className="pricing-tab__fact-body is-warn">
                      <b>You cannot be paid yet</b>
                      <span>
                        Set up a payout account before charging — until then a checkout for
                        this app is refused, so nobody can buy it.
                      </span>
                      <Button onClick={toPayouts}>Set up payouts</Button>
                    </div>
                  ) : !canCharge ? (
                    <div className="pricing-tab__fact-body is-warn">
                      <b>Your payout account is not finished</b>
                      {requirementLabels(payout.account.requirements_due).length > 0 && (
                        <span>Stripe still needs: {requirementLabels(payout.account.requirements_due).join(', ')}.</span>
                      )}
                      <Button onClick={toPayouts}>Finish setup</Button>
                    </div>
                  ) : (
                    <b>Ready to sell</b>
                  )}
                </div>
                {canCharge && (
                  <div className="pricing-tab__fact">
                    <span>Merchant of record</span>
                    <b>{payout.charge_mode === 'destination' ? 'ofSelf — we handle tax and disputes' : 'You — your name is on the statement'}</b>
                  </div>
                )}
                {feeBps != null && (
                  <div className="pricing-tab__fact">
                    <span>Platform fee</span>
                    <b>{feeBps > 0 ? `${(feeBps / 100).toFixed(feeBps % 100 ? 2 : 0)}%` : 'None'}</b>
                  </div>
                )}
                {/* The consequence a developer is most likely to be surprised by,
                    stated before they hit it rather than in the error that stops a save. */}
                {isPaid(app?.pricing) && (
                  <p className="pricing-tab__help">
                    Raising a price asks more of people who already pay, so committing it will
                    ask you to choose: <b>grandfather</b> them at the price they agreed to, or
                    <b> cancel</b> their access so they re-authorize at the new one.
                  </p>
                )}
              </>
            )}
          </div>

          <div className={`pricing-tab__preview${showError ? ' is-invalid' : ''}`} aria-label="Consent card preview">
            <div className="pricing-tab__card-who">
              {app?.logo_url
                ? <img src={app.logo_url} alt="" />
                : <span aria-hidden="true">{initials}</span>}
              <b>{app?.name}</b>
            </div>
            {paid && !typed ? (
              <span className="pricing-tab__preview-hint">Enter a price to see it here.</span>
            ) : preview && preview.model !== 'free' ? (
              <>
                <span className="pricing-tab__preview-head">{preview.headline}</span>
                {preview.alternate && <span>{preview.alternate}</span>}
                {preview.detail && <span>{preview.detail}</span>}
              </>
            ) : !paid ? (
              <span className="pricing-tab__preview-hint">No price line</span>
            ) : <span className="pricing-tab__preview-head">—</span>}
            <span className="pricing-tab__card-go" aria-hidden="true">Authorize</span>
          </div>
        </div>
        {showError && <p className="pricing-tab__error">{previewError}</p>}
      </section>

      {/* The stage bar appears with the first change, as on Data it asks for. */}
      {dirty && (
        <div className="pricing-tab__stagebar" role="region" aria-label="Unsaved price change">
          <div>
            <b>Price changed, not staged</b>
            <span>Review it with your other changes and commit when you are ready.</span>
          </div>
          <div className="pricing-tab__stagebar-acts">
            <button type="button" className="pricing-tab__discard" onClick={reset} disabled={saving}>Discard</button>
            <Button onClick={save} disabled={saving || Boolean(previewError)}>
              {saving ? 'Staging…' : 'Stage price'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** The form's fields from a stored pricing blob (minor units → what a person types). */
function hydrate(pricing) {
  const p = pricing || { model: 'free' };
  const cur = p.currency || 'GBP';
  return {
    model: p.model || 'free',
    currency: cur,
    amount: toMajorInput(p.amount, cur),
    monthly: toMajorInput(p.monthly, cur),
    annual: toMajorInput(p.annual, cur),
    trialDays: p.trial_days ? String(p.trial_days) : '',
  };
}

/** The blob as the API wants it, from the form's fields. */
function buildBlob({ model, currency, amount, monthly, annual, trialDays }) {
  if (model === 'free') return { model: 'free' };
  if (model === 'one_time') {
    return { model: 'one_time', currency, amount: toMinor(amount, currency) };
  }
  const out = { model: 'subscription', currency };
  if (monthly !== '') out.monthly = toMinor(monthly, currency);
  if (annual !== '') out.annual = toMinor(annual, currency);
  if (trialDays !== '') out.trial_days = Number(trialDays);
  return out;
}
